/**
 * PasteUrlModal Component
 * Modal for manual Spotify URL input when clipboard permissions are denied
 *
 * Features:
 * - Real-time URL validation for tracks and playlists
 * - Color-coded border feedback (neutral/valid/invalid)
 * - Auto-detects URL type (track or playlist)
 * - Submit on Enter key
 * - Loading state during submission
 * - Inline validation messages
 * - Example URL formats
 *
 * Usage:
 * ```tsx
 * <PasteUrlModal
 *   open={showModal}
 *   onClose={() => setShowModal(false)}
 *   onSubmit={async (url) => {
 *     await handleImport(url);
 *     setShowModal(false);
 *   }}
 * />
 * ```
 */

import { useState } from 'react';
import { Modal, ModalActions } from './Modal';
import { Button } from './Button';
import { parseTrackFromClipboard, parsePlaylistFromClipboard } from '../../utils/spotify-import';

/**
 * CheckIcon Component
 * Inline SVG for validation success indicator
 */
const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export interface PasteUrlModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when URL is submitted (parent handles success/error) */
  onSubmit: (url: string) => Promise<void>;
}

/**
 * Validation result type
 */
interface ValidationResult {
  valid: boolean | null; // null = empty/neutral, true = valid, false = invalid
  type: 'track' | 'playlist' | null; // Type of detected URL
}

/**
 * Validate Spotify URL using existing parse functions
 *
 * @param url - Input URL string
 * @returns Validation result with type detection
 */
function validateUrl(url: string): ValidationResult {
  // Empty input → neutral state
  if (!url.trim()) {
    return { valid: null, type: null };
  }

  // Try parsing as track first
  const trackId = parseTrackFromClipboard(url);
  if (trackId) {
    return { valid: true, type: 'track' };
  }

  // Try parsing as playlist
  const playlistId = parsePlaylistFromClipboard(url);
  if (playlistId) {
    return { valid: true, type: 'playlist' };
  }

  // Invalid format
  return { valid: false, type: null };
}

/**
 * PasteUrlModal Component
 */
export function PasteUrlModal({ open, onClose, onSubmit }: PasteUrlModalProps) {
  // State
  const [url, setUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Real-time validation
  const validation = validateUrl(url);

  // Can submit only if valid URL
  const canSubmit = validation.valid;

  /**
   * Determine border color class based on validation state
   */
  const getBorderColorClass = (): string => {
    if (validation.valid === null) {
      // Empty/neutral
      return 'border-zinc-700 focus:ring-zinc-500';
    } else if (validation.valid) {
      // Valid
      return 'border-emerald-500 focus:ring-emerald-500';
    } else {
      // Invalid
      return 'border-red-500 focus:ring-red-500';
    }
  };

  /**
   * Handle form submission
   * Calls parent's onSubmit callback (parent handles closing modal)
   */
  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await onSubmit(url);
      // Parent handles success (closing modal, showing toast, etc.)
    } catch (error) {
      console.error('Submit failed:', error);
      // Parent handles error display
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle Enter key press
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canSubmit && !isSubmitting) {
      handleSubmit();
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Paste Spotify URL" className="w-full max-w-lg">
      {/* Form Content */}
      <div className="p-6 space-y-4">
        {/* Instructions */}
        <p className="text-sm text-zinc-400">
          Your browser blocked clipboard access. Please paste the Spotify URL manually.
        </p>

        {/* Input with real-time validation */}
        <div className="space-y-2">
          <label htmlFor="spotify-url" className="block text-sm font-medium text-zinc-300">
            Spotify URL
          </label>
          <input
            id="spotify-url"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`
              w-full px-3 py-2 bg-zinc-800 border rounded-lg text-zinc-200
              placeholder-zinc-500 focus:outline-none focus:ring-2
              transition-colors
              ${getBorderColorClass()}
            `}
            placeholder="https://open.spotify.com/track/... or playlist/..."
            autoFocus
          />

          {/* Validation feedback messages */}
          {validation.valid && (
            <p className="text-xs text-red-400">
              Invalid Spotify URL. Please check the format.
            </p>
          )}
          {validation.valid && validation.type && (
            <p className="text-xs text-emerald-400 flex items-center gap-1">
              <CheckIcon className="w-4 h-4" />
              Valid {validation.type} URL detected
            </p>
          )}
        </div>

        {/* Example URLs */}
        <div className="text-xs text-zinc-500 space-y-1">
          <p className="font-medium">Supported formats:</p>
          <p>• Track: https://open.spotify.com/track/abc123...</p>
          <p>• Playlist: https://open.spotify.com/playlist/xyz789...</p>
        </div>
      </div>

      {/* Actions */}
      <ModalActions>
        <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          isLoading={isSubmitting}
        >
          {isSubmitting ? 'Pasting...' : 'Paste'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
